import React, { useState } from 'react';
import { ArrowUpRight, Sparkles, Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from 'lucide-react';
import Reveal from '../components/Reveal';
import { CONTACT_INFO } from '../mock/data';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="block text-xs text-white/60 uppercase tracking-wider mb-2">{label}</span>
    <input
      {...props}
      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-cyan-400 focus:bg-white/10 transition-colors"
    />
  </label>
);

const TextArea = ({ label, ...props }) => (
  <label className="block">
    <span className="block text-xs text-white/60 uppercase tracking-wider mb-2">{label}</span>
    <textarea
      {...props}
      rows={5}
      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-cyan-400 focus:bg-white/10 transition-colors resize-none"
    />
  </label>
);

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '', role: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in your name, email and message');
      return;
    }
    setLoading(true);
    const backend = process.env.REACT_APP_BACKEND_URL;
    const canUseApi = backend && !/localhost|preview\.emergentagent/.test(backend) ? true : !!backend;

    // Helper: open mailto fallback with prefilled content
    const openMailtoFallback = () => {
      const subject = encodeURIComponent(`Inquiry from ${form.name}${form.company ? ' — ' + form.company : ''}`);
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company || '-'}\nRole: ${form.role || '-'}\n\nMessage:\n${form.message}`
      );
      window.location.href = `mailto:${CONTACT_INFO.email}?subject=${subject}&body=${body}`;
    };

    try {
      if (!backend) throw new Error('backend-not-configured');
      const res = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        role: form.role.trim() || null,
        message: form.message.trim(),
        inquiry_type: 'General',
      }, { timeout: 10000 });
      if (res.data?.email_sent) {
        toast.success('Message sent', { description: 'A confirmation is on its way to your inbox. Our team will follow up within 24 hours.' });
      } else {
        toast.success('Message received', { description: 'Our team will reach out within 24 hours.' });
      }
      setSent(true);
      setForm({ name: '', email: '', company: '', role: '', message: '' });
    } catch (err) {
      // Graceful fallback: open the user's mail client with the message pre-filled
      toast('Opening your email app…', { description: 'Send the pre-filled message to complete your inquiry.' });
      openMailtoFallback();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="hero-glow" />
        <div className="container-x relative">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-300 text-xs font-medium uppercase tracking-widest mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Contact
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-display text-5xl md:text-6xl font-bold text-white leading-[1.05] max-w-4xl">
              Let's start a <span className="shimmer-text">conversation.</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-white/60 text-lg max-w-2xl mt-6">
              Talk to our team about your hiring or technology needs. First qualified shortlist within 48 hours — no long-winded discovery calls.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          <div className="grid lg:grid-cols-3 gap-8">
            <Reveal>
              <div className="glass rounded-2xl p-8 space-y-6 h-full">
                <h3 className="font-display text-xl font-semibold text-white">Reach us directly</h3>
                <div className="space-y-5 text-sm">
                  <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-start gap-3 group">
                    <div className="w-10 h-10 rounded-lg bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center text-cyan-300 shrink-0">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Email</div>
                      <div className="text-white group-hover:text-cyan-300 transition-colors">{CONTACT_INFO.email}</div>
                    </div>
                  </a>
                  <a href={`tel:${CONTACT_INFO.phone}`} className="flex items-start gap-3 group">
                    <div className="w-10 h-10 rounded-lg bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center text-cyan-300 shrink-0">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Phone</div>
                      <div className="text-white group-hover:text-cyan-300 transition-colors">{CONTACT_INFO.phone}</div>
                    </div>
                  </a>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center text-cyan-300 shrink-0">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Address</div>
                      <div className="text-white">{CONTACT_INFO.address}</div>
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-white/5">
                  <div className="text-xs text-white/50 uppercase tracking-wider mb-2">Response time</div>
                  <div className="flex items-center gap-2 text-cyan-300">
                    <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                    <span className="text-sm font-medium">Typically within 24 hours</span>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="lg:col-span-2">
                <form onSubmit={submit} className="glass rounded-2xl p-8 space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Full name" placeholder="Jane Doe" value={form.name} onChange={handle('name')} />
                    <Field label="Work email" type="email" placeholder="jane@company.com" value={form.email} onChange={handle('email')} />
                    <Field label="Company" placeholder="Acme Inc." value={form.company} onChange={handle('company')} />
                    <Field label="Your role" placeholder="Head of Talent" value={form.role} onChange={handle('role')} />
                  </div>
                  <TextArea label="How can we help?" placeholder="Tell us about the role, skills or project you need help with…" value={form.message} onChange={handle('message')} />
                  <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
                    {sent ? (
                      <div className="flex items-center gap-2 text-cyan-300 text-sm">
                        <CheckCircle2 className="w-4 h-4" /> Thanks — message received. We’ll be in touch.
                      </div>
                    ) : <span className="text-xs text-white/40">By submitting, you agree to be contacted about your inquiry.</span>}
                    <button type="submit" disabled={loading} className="btn-primary inline-flex items-center gap-1.5 px-6 py-3 rounded-full text-sm disabled:opacity-70 disabled:cursor-not-allowed">
                      {loading ? (<>Sending <Loader2 className="w-4 h-4 animate-spin" /></>) : (<>Send message <Send className="w-4 h-4" /></>)}
                    </button>
                  </div>
                </form>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
